<template>
	<div>
		<nta-bubbly-modal
			:show="compProps.show"
			ref="bubblyModal"
			:data="bubbles"
			:color-range="{
				// @ts-ignore
				light: $vuetify.theme.name === 'dark' ? '#595959' : '#ECEFF4',
				// @ts-ignore
				dark: $vuetify.theme.name === 'dark' ? '#1C1C1D' : '#595959',
			}"
			:title="title"
			:tooltips="false"
			:searchable="true"
			:fixed-tooltip="true"
			:tooltip-offset-y="104"
			breadcrumb-color="rgb(var(--v-theme-primary))"
			:symbol-lang="domainStore.languagePreference"
			@node-click="nodeClick"
			@search-hit-click="onSearchHitClick"
			@done="() => done()"
		/>
		<attribute-modal
			:show="attributeModalData.show"
			:attribute-id="attributeModalData.attributeId"
			@set="render"
			@done="attributeModalData.show = false"
		/>
		<composed-attributes-modal
			v-if="nestedModal.show"
			:show="nestedModal.show"
			:dataobject-id="nestedModal.dataobjectId"
			:parent-id="nestedModal.parentId"
			:is-nested="true"
			@done="nestedModal.show = false"
		/>
		<confirmation-modal
			:modal="addInstanceData"
			@confirmation="onConfirmAdd"
			@done="addInstanceData.show = false"
		/>
		<confirmation-modal
			:modal="delInstanceData"
			@confirmation="onConfirmDel"
			@done="delInstanceData.show = false"
		/>
	</div>
</template>

<script setup lang="ts">
import type { BubblyItem, BubblyNode } from "@lr-notitia/bubbly-d3";
import { computed, onMounted, ref, watch, onUnmounted } from "vue";
import { useGraphStore } from "../../../store/graph";
import { useDomainStore } from "../../../store/domain";
import { useWorkerStore } from "../../../store/worker";
import { useSymbolsStore } from "../../../store/symbols";
import AttributeModal from "./AttributeModal.vue";
import ConfirmationModal from "../../../components/ConfirmationModal.vue";

import "@lr-notitia/bubbly-d3/style.css";
import { ConceptDataobject, InstanceDataobject } from "@lr-notitia/notitia-operator/schemas";

export interface Bubble extends BubblyItem {
	synonyms: string[];
	depth: number;
	tooltipSuffix: string;
}

const graphStore = useGraphStore();
const domainStore = useDomainStore();
const symbolsStore = useSymbolsStore();
const workerStore = useWorkerStore();
let worker = ref<any>(null);

const compProps = defineProps<{
	show: boolean;
	dataobjectId: string;
	isNested: boolean; // if true, the root dataobject is treated as a concept dataobject, otherwise as an instance dataobject
	parentId: string;
}>();
const emits = defineEmits(["done"]);

const bubbles = ref<Bubble[]>([]);

const bubblyModal = ref<any>(null);

const attributeModalData = ref<{ show: boolean; attributeId: string }>({ show: false, attributeId: "" });
const nestedModal = ref<{ show: boolean; dataobjectId: string; parentId: string }>({
	show: false,
	dataobjectId: "",
	parentId: "",
});
const addInstanceData = ref<{ show: boolean; message: string; data: {} }>({
	show: false,
	message: "",
	data: {},
});
const delInstanceData = ref<{ show: boolean; message: string; data: { id: string } }>({
	show: false,
	message: "",
	data: { id: "" },
});

const dataobject = computed(() => {
	if (compProps.isNested) return graphStore.concept.dataobject.byId(compProps.dataobjectId);
	else return graphStore.instance.dataobject.byId(compProps.dataobjectId);
});
const title = computed(() => {
	if (!dataobject.value) return "";
	if (compProps.isNested) {
		return symbolsStore.term({
			id: (dataobject.value as ConceptDataobject).refersTo,
			terminology: (dataobject.value as ConceptDataobject).terminology,
		});
	} else {
		let concept = graphStore.concept.dataobject.byId((dataobject.value as InstanceDataobject).concept);
		if (!concept) return "";
		return symbolsStore.term({ id: concept.refersTo, terminology: concept.terminology });
	}
});

function done() {
	emits("done");
}

function nodeClick(event: PointerEvent, node: BubblyNode) {
	if (isDataobjectNode(node)) return;
	if (isHasAGroupNode(node)) {
		openHasAGroup(node);
	}
	if (isAttributeNode(node) && !isHasAGroupNode(node)) {
		if (isAddInstanceNode(node)) {
			addInstanceData.value = {
				show: true,
				message: "Do you want to add a new " + (node.parent?.data.name || "") + "?",
				data: {},
			};
		} else {
			attributeModalData.value = { show: true, attributeId: node.data.id };
		}
	}
}
function nodeContext(event: PointerEvent, node: BubblyNode) {
	event.preventDefault();
	if (ishasAGroupChild(node)) {
		delInstanceData.value = {
			show: true,
			message: "Do you want to remove " + (node.data.name || "") + "?",
			data: { id: node.data.id },
		};
	}
}

function isDataobjectNode(node: BubblyNode): boolean {
	return !!node.children?.length;
}
function isAttributeNode(node: BubblyNode): boolean {
	return !node.children?.length;
}
function isHasAGroupNode(node: BubblyNode): boolean {
	return node.data.shape === "hexagon";
}
function ishasAGroupChild(node: BubblyNode): boolean {
	return isDataobjectNode(node) && !!graphStore.concept.dataobject.byId(node.parent?.data.id || "");
}
function isAddInstanceNode(node: BubblyNode): boolean {
	return node.data.id === "add-instance";
}

function onSearchHitClick(id: string, parentId: string = "") {
	if (parentId) attributeModalData.value = { show: true, attributeId: id };
}

function openHasAGroup(node: BubblyNode) {
	let conceptId = node.data.id;
	let parentId = node.parent?.data.id || ""; // parent of the Hexagon node
	if (bubblyModal.value?.bubbly?.simulation.focused === node.parent?.data.id) {
		nestedModal.value = { show: true, dataobjectId: conceptId, parentId: parentId };
	} else {
		setTimeout(() => {
			nestedModal.value = { show: true, dataobjectId: conceptId, parentId: parentId };
		}, 750);
	}
}

function onConfirmAdd(confirm: boolean): void {
	if (confirm)
		graphStore.instance.dataobject.add(compProps.dataobjectId, "", compProps.parentId).finally(() => {
			addInstanceData.value = { show: false, message: "", data: {} };
			render();
		});
}
function onConfirmDel(confirm: boolean): void {
	if (confirm)
		graphStore.instance.dataobject.remove(delInstanceData.value.data.id).finally(() => {
			delInstanceData.value = { show: false, message: "", data: { id: "" } };
			render();
		});
}

function postWorker() {
	worker.value.postMessage({
		graph: JSON.parse(JSON.stringify(graphStore.graph)),
		symbols: JSON.parse(JSON.stringify(symbolsStore.symbols)),
		dataobject: JSON.parse(JSON.stringify(dataobject.value)),
		isNested: compProps.isNested,
		languagePreference: domainStore.languagePreference,
		parentId: compProps.parentId,
		role: domainStore.account.role,
	});
}

function render() {
	postWorker();
}

watch(
	() => bubblyModal.value?.bubbly?.simulation,
	(simulation) => {
		if (simulation && compProps.show) {
			if (compProps.isNested) {
				simulation.config.useProxyNodes = false;
			}
			simulation.on("node:contextmenu", (e: any, node: any) => nodeContext(e, node));
		}
	}
);

onMounted(async () => {
	worker.value = await workerStore.spawn("bubbly");
	worker.value.addEventListener("message", (event: any) => {
		bubbles.value = event.data.result;
	});
	render();
});
onUnmounted(() => {
	worker.value.terminate();
	workerStore.releaseWorker("bubbly");
});
</script>

<style lang="scss">
g.icons {
	pointer-events: none !important;
}
</style>
